import { normalizeGroupIds, UUID_PATTERN, ValidationError } from './validation';

/**
 * Turning "the group the operator means" into "the object ID authorization is keyed on".
 *
 * Entra display names are **mutable and not unique**, so they are an input convenience only. A name
 * is resolved to an object ID once, at the moment an app is written, and the registry stores the ID
 * - never the name. That keeps every property the pre-token trigger depends on intact: it still
 * compares IDs, a renamed group still gates the same app, and two groups sharing a display name can
 * never silently swap which one grants access.
 *
 * The lookup is injected rather than imported so this module stays free of the AWS SDK and of
 * graph.ts, in the same spirit as validation.ts.
 */

export interface GroupSummaryLike {
  id: string;
  displayName: string;
}

/** Returns groups whose display name starts with the argument. Implemented by GraphClient. */
export type GroupNameLookup = (name: string) => Promise<GroupSummaryLike[]>;

/** Whether a reference is already an object ID, and so needs no lookup. */
export function isGroupId(ref: string): boolean {
  return UUID_PATTERN.test(ref.trim());
}

/** How many near misses to name in a not-found message before it stops being helpful. */
const MAX_SUGGESTIONS = 5;

/**
 * Resolves a mixed list of object IDs and display names to object IDs.
 *
 * IDs pass straight through, which matters for more than speed: a caller that supplies only IDs
 * never invokes the lookup, so it needs neither Graph reachability nor the credentials to reach it.
 *
 * Resolution is sequential. At most MAX_GROUP_IDS names are involved, and doing them in order means
 * the error names the first bad reference rather than whichever request happened to fail first.
 *
 * The result goes through normalizeGroupIds, so the caps, the format check and the de-duplication
 * are the same ones a directly-supplied ID list gets. This function only widens what may be
 * *typed*; it does not widen what may be *stored*.
 */
export async function resolveGroupRefs(
  lookup: GroupNameLookup,
  refs: string[],
): Promise<string[]> {
  const ids: string[] = [];

  for (const raw of refs) {
    const ref = raw.trim();
    if (!ref) {
      throw new ValidationError('An empty value is not a group name or ID');
    }
    ids.push(isGroupId(ref) ? ref : (await resolveGroupName(lookup, ref)).id);
  }

  return normalizeGroupIds(ids);
}

/**
 * Resolves one display name to exactly one group, or throws.
 *
 * **An exact (case-insensitive) name match is required.** A unique prefix match is deliberately not
 * accepted: "Field" matching only "Field Staff" today would silently become ambiguous the day
 * someone creates "Field Staff Leads", and the failure would land on an authorization rule. Prefix
 * candidates are used only to suggest names in the error.
 *
 * Ambiguity is a hard error rather than a first-match, for the same reason.
 */
export async function resolveGroupName(
  lookup: GroupNameLookup,
  name: string,
): Promise<GroupSummaryLike> {
  const wanted = name.trim();
  const candidates = await lookup(wanted);
  const folded = wanted.toLowerCase();
  const exact = candidates.filter((c) => c.displayName.trim().toLowerCase() === folded);

  if (exact.length === 1) return exact[0];

  if (exact.length > 1) {
    throw new ValidationError(
      `${exact.length} Entra groups are named "${wanted}". Pass the object ID of the one you mean:\n` +
        exact.map((c) => `  ${c.id}  ${c.displayName}`).join('\n'),
    );
  }

  if (candidates.length === 0) {
    throw new ValidationError(
      `No Entra group is named "${wanted}". Names must match exactly (case does not matter); ` +
        'search for one with: npm run broker -- group:find <partial name>',
    );
  }

  const suggestions = candidates.slice(0, MAX_SUGGESTIONS).map((c) => `  ${c.displayName}`);
  const more =
    candidates.length > MAX_SUGGESTIONS
      ? `\n  …and ${candidates.length - MAX_SUGGESTIONS} more`
      : '';
  throw new ValidationError(
    `No Entra group is named exactly "${wanted}". Did you mean:\n${suggestions.join('\n')}${more}`,
  );
}
